import type { FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/format'
import { Slider } from '@/components/ui/slider'

interface MortgageEstimateProps {
    price: number
}

// California averages, used only for a rough estimate.
const PROPERTY_TAX_RATE = 0.011
const INSURANCE_RATE = 0.003

export const MortgageEstimate: FC<MortgageEstimateProps> = ({ price }): ReactNode => {
    const rateId = useId()
    const [downPercent, setDownPercent] = useState(20)
    const [rate, setRate] = useState('6.25')
    const [years, setYears] = useState<15 | 30>(30)

    const loan = price * (1 - downPercent / 100)
    const monthlyRate = (Number(rate) || 0) / 100 / 12
    const payments = years * 12
    const principalAndInterest = monthlyRate > 0
        ? loan * monthlyRate / (1 - Math.pow(1 + monthlyRate, -payments))
        : loan / payments
    const taxes = price * PROPERTY_TAX_RATE / 12
    const insurance = price * INSURANCE_RATE / 12
    const total = principalAndInterest + taxes + insurance

    const parts = [
        { label: 'Principal and interest', value: principalAndInterest, color: 'bg-primary' },
        { label: 'Property tax', value: taxes, color: 'bg-foreground' },
        { label: 'Home insurance', value: insurance, color: 'bg-muted-foreground/40' }
    ]

    return (
        <div>
            <p className='text-3xl font-bold tracking-tight'>
                {formatPrice(Math.round(total))}
                <span className='text-base font-normal text-muted-foreground'> per month</span>
            </p>

            <div className='mt-4 flex h-3 overflow-hidden rounded-full' aria-hidden='true'>
                {parts.map(part => (
                    <span key={part.label} className={part.color} style={{ width: `${total > 0 ? (part.value / total) * 100 : 0}%` }} />
                ))}
            </div>

            <ul className='mt-4 space-y-2 text-sm'>
                {parts.map(part => (
                    <li key={part.label} className='flex items-center justify-between gap-4'>
                        <span className='flex items-center gap-2'>
                            <span className={cn('size-2.5 rounded-full', part.color)} aria-hidden='true' />
                            {part.label}
                        </span>
                        <span className='font-medium'>{formatPrice(Math.round(part.value))}</span>
                    </li>
                ))}
            </ul>

            <div className='mt-6 grid gap-6 sm:grid-cols-2'>
                <div>
                    <div className='flex items-baseline justify-between text-sm'>
                        <span className='font-medium'>Down payment</span>
                        <span className='text-muted-foreground'>
                            <span>{downPercent}</span>% · {formatPrice(Math.round(price * downPercent / 100))}
                        </span>
                    </div>
                    <Slider
                        value={downPercent}
                        min={5}
                        max={50}
                        step={1}
                        onValueChange={value => setDownPercent(Array.isArray(value) ? value[0] : value)}
                        thumbLabels={['Down payment percent']}
                        formatValueText={value => `${value} percent`}
                    />
                </div>

                <div className='grid grid-cols-2 gap-3'>
                    <label htmlFor={rateId} className='text-sm'>
                        <span className='font-medium'>Interest rate</span>
                        <span className='mt-1 flex items-center rounded-xl border border-input px-3 focus-within:border-foreground focus-within:ring-1 focus-within:ring-foreground'>
                            <input
                                id={rateId}
                                inputMode='decimal'
                                value={rate}
                                onChange={event => setRate(event.target.value.replace(/[^0-9.]/g, '').slice(0, 5))}
                                className='h-10 w-full bg-transparent outline-none'
                            />
                            <span className='text-muted-foreground'>%</span>
                        </span>
                    </label>

                    <div className='text-sm'>
                        <span className='font-medium'>Loan term</span>
                        <div className='mt-1 grid h-[2.625rem] grid-cols-2 rounded-xl border border-input p-1' role='radiogroup' aria-label='Loan term'>
                            {([30, 15] as const).map(term => (
                                <button
                                    key={term}
                                    type='button'
                                    role='radio'
                                    aria-checked={years === term}
                                    onClick={() => setYears(term)}
                                    className={cn('rounded-lg text-sm font-medium transition', years === term ? 'bg-foreground text-background' : 'hover:bg-muted')}
                                >
                                    {term} yr
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <p className='mt-4 text-xs text-muted-foreground'>
                A rough estimate using typical California tax and insurance costs. Your lender will give you exact numbers.
            </p>
        </div>
    )
}
